import React from 'react';
import classNames from 'classnames';

import { ExternalLink, Logo, NamedLink } from '../../components';
import { useIntl } from '../../util/reactIntl';
import { useLocation } from 'react-router-dom';

import css from './LinkedLogo.module.css';

/**
 * This component returns a clickable logo.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.logoClassName andd more style rules in addtion to css.logo
 * @param {string?} props.logoImageClassName overwrite components own css.root
 * @param {('desktop' | 'mobile')} props.layout
 * @param {Object} props.linkToExternalSite
 * @param {string} props.linkToExternalSite.href
 * @param {string?} props.alt alt text for logo image
 * @returns {JSX.Element} linked logo component
 */
const LinkedLogo = props => {
  const {
    className,
    rootClassName,
    logoClassName,
    logoImageClassName,
    layout = 'desktop',
    linkToExternalSite,
    alt,
    ...rest
  } = props;
  const intl = useIntl();
  const location = useLocation();
  const classes = classNames(rootClassName || css.root, className);
  // Note: If there's an external href, use it, but if we are already on that path, don't navigate.
  const isDutch = (intl?.locale || '').toLowerCase().startsWith('nl');
  const currentPath = location?.pathname || '';

  if (linkToExternalSite?.href) {
    const externalHref = linkToExternalSite.href;
    if (currentPath === externalHref) {
      return (
        <span className={classes} aria-current="page" {...rest}>
          <Logo
            layout={layout}
            className={logoClassName}
            logoImageClassName={logoImageClassName}
            alt={alt}
          />
        </span>
      );
    }
    return (
      <ExternalLink className={classes} href={externalHref} target="_self" {...rest}>
        <Logo
          layout={layout}
          className={logoClassName}
          logoImageClassName={logoImageClassName}
          alt={alt}
        />
      </ExternalLink>
    );
  }

  // Internal language-aware routing. If already on target path, render non-link wrapper.
  const targetInternalPath = isDutch ? '/p/nl-home' : '/en-home';
  if (currentPath === targetInternalPath) {
    return (
      <span className={classes} aria-current="page" {...rest}>
        <Logo
          layout={layout}
          className={logoClassName}
          logoImageClassName={logoImageClassName}
          alt={alt}
        />
      </span>
    );
  }

  return isDutch ? (
    <NamedLink className={classes} name="CMSPage" params={{ pageId: 'nl-home' }} {...rest}>
      <Logo
        layout={layout}
        className={logoClassName}
        logoImageClassName={logoImageClassName}
        alt={alt}
      />
    </NamedLink>
  ) : (
    <NamedLink className={classes} name="EnglishLandingPage" {...rest}>
      <Logo
        layout={layout}
        className={logoClassName}
        logoImageClassName={logoImageClassName}
        alt={alt}
      />
    </NamedLink>
  );
};

export default LinkedLogo;
